import { cosA, dist2, iatan2, idiv, imax, isqrt, sinA, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import { ROW } from '../hex.ts';
import type { BattleState, Boar, Enemy } from '../types.ts';
import { damageEnemy, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';

/**
 * The drive (El Cuerno 1, docs/SAGAS.md): stone boars to take across the field to the clan's pen. They shy away from
 * the heroes, so they are herded from behind like a sheepdog does; they bolt from nightmares, any which way. A boar
 * left alone too long brings wolves. An ogre's roar sends the whole herd running away from it, over whatever
 * nightmares are in the way. Won with every boar penned or fallen, and enough of them in the pen.
 */

const HERD_ARC = 341; // ±120° of the 1024 steps: how far off a bolting boar may run from straight away.

/** (dx, dy) scaled to `px` long (nothing if it has no length). */
const scaled = (dx: number, dy: number, px: number): [number, number] => {
  const d = isqrt(dx * dx + dy * dy);
  return d === 0 ? [0, 0] : [idiv(dx * px, d), idiv(dy * px, d)];
};

const step = (b: Boar, dx: number, dy: number): void => {
  if (dx === 0 && dy === 0) return;
  b.x += dx;
  b.y += dy;
  b.facing = iatan2(dy, dx);
  b.moving = true;
};

/** Away from (x, y), turned a little at random (a bolt is never quite straight). */
const bolt = (s: BattleState, b: Boar, x: number, y: number, ticks: number, px: number): void => {
  const a = iatan2(b.y - y, b.x - x) + rngInt(s.rng, HERD_ARC) - idiv(HERD_ARC, 2);
  b.dx = idiv(cosA(a) * px, 10000);
  b.dy = idiv(sinA(a) * px, 10000);
  b.panic = ticks;
};

/** The pen's spot for the n-th boar in it: a small spiral around its middle. */
const penSpot = (pen: { x: number; y: number }, r: number, n: number): { x: number; y: number } => {
  if (n === 0) return { x: pen.x, y: pen.y };
  const ring = n <= 5 ? 1 : 2;
  const a = idiv(n * 1024 * 3, 8) + ring * 97;
  const d = idiv(r * ring * 3, 8);
  return { x: pen.x + idiv(cosA(a) * d, 10000), y: pen.y + idiv(sinA(a) * d, 10000) };
};

/** An ogre roared: every boar near enough runs straight away from it. */
export const herdStampede = (s: BattleState, e: Enemy): void => {
  const r = s.rules?.herd;
  const h = s.herd;
  // A herd that just ran won't run again so soon.
  if (!r || !h || h.stampedeCd > 0) return;
  let any = false;
  for (const b of h.boars) {
    if (b.penned || !withinRange(b.x, b.y, e.x, e.y, r.stampede.reach)) continue;
    const [dx, dy] = scaled(b.x - e.x, b.y - e.y, r.stampede.speed);
    if (dx === 0 && dy === 0) continue;
    b.dx = dx;
    b.dy = dy;
    b.rush = r.stampede.ticks;
    b.panic = 0;
    b.hit = [];
    any = true;
  }
  if (!any) return;
  h.stampedeCd = r.stampede.cd;
  ev(s, { t: 'herd', kind: 'stampede', x: e.x, y: e.y });
};

export const herdTick = (s: BattleState): void => {
  const r = s.rules?.herd;
  if (!r) return;
  if (!s.herd) {
    // The herd comes out in a loose bunch around its start.
    const boars: Boar[] = [];
    for (let i = 0; i < r.boars; i++) {
      const a = idiv(i * 1024 * 5, 13);
      const d = 250 + (i % 3) * 330;
      boars.push({ id: i + 1, x: r.start.x + idiv(cosA(a) * d, 10000), y: r.start.y + idiv(sinA(a) * d, 10000), hp: r.hp, maxHp: r.hp, facing: 0, moving: false, panic: 0, rush: 0, dx: 0, dy: 0, alone: 0, penned: false, hit: [] });
    }
    s.herd = { boars, penned: 0, lost: 0, wolfCd: 0, stampedeCd: 0 };
  }
  const st = s.herd;
  if (!s.started) return;
  if (st.wolfCd > 0) st.wolfCd--;
  if (st.stampedeCd > 0) st.stampedeCd--;
  // The field ends at the cliff over the sea, a hex short of the map's edge.
  const out = (s.map.radius - 1) * ROW;
  const loose = st.boars.filter((b) => !b.penned);
  // The herd's middle: a boar that strays drifts back to it.
  let cx = 0;
  let cy = 0;
  for (const b of loose) {
    cx += b.x;
    cy += b.y;
  }
  if (loose.length) {
    cx = idiv(cx, loose.length);
    cy = idiv(cy, loose.length);
  }
  const standing = s.heroes.filter((x) => x.downTicks === 0);
  const live = s.enemies.filter((e) => e.hp > 0 && e.kind !== 'boneWall');
  const keep: Boar[] = [];
  for (const b of st.boars) {
    if (b.penned) {
      keep.push(b);
      continue;
    }
    b.moving = false;
    if (b.rush > 0) {
      // Stampeding: nothing turns it; whatever nightmare it runs over is knocked down.
      b.rush--;
      step(b, b.dx, b.dy);
      for (const e of live) {
        if (e.hp <= 0 || b.hit.includes(e.id) || !withinRange(e.x, e.y, b.x, b.y, r.stampede.hit)) continue;
        b.hit.push(e.id);
        e.stun = imax(e.stun, r.stampede.stun);
        damageEnemy(s, e, r.stampede.damage, { pid: '', ignoresArmor: true, noReact: true });
      }
    } else if (b.panic > 0) {
      b.panic--;
      step(b, b.dx, b.dy);
    } else {
      const scary = live.find((e) => withinRange(e.x, e.y, b.x, b.y, r.panic.range));
      if (scary) {
        bolt(s, b, scary.x, scary.y, r.panic.ticks, r.panic.speed);
        ev(s, { t: 'herd', kind: 'panic', x: b.x, y: b.y });
        step(b, b.dx, b.dy);
      } else {
        // Shying from the heroes near it: the closer, the stronger the push.
        let px = 0;
        let py = 0;
        for (const h of standing) {
          const dx = b.x - h.x;
          const dy = b.y - h.y;
          const d = isqrt(dx * dx + dy * dy);
          if (d >= r.scare) continue;
          const w = r.scare - d;
          if (d === 0) px += w;
          else {
            px += idiv(dx * w, d);
            py += idiv(dy * w, d);
          }
        }
        if (px !== 0 || py !== 0) {
          const [dx, dy] = scaled(px, py, r.speed);
          step(b, dx, dy);
        } else if (!withinRange(b.x, b.y, cx, cy, r.flock)) {
          const [dx, dy] = scaled(cx - b.x, cy - b.y, r.drift);
          step(b, dx, dy);
        }
      }
    }
    // Not on top of each other.
    for (const o of loose) {
      if (o === b || o.penned || !withinRange(o.x, o.y, b.x, b.y, r.apart)) continue;
      const [dx, dy] = scaled(b.x - o.x || b.id - o.id, b.y - o.y, idiv(r.apart, 12));
      b.x += dx;
      b.y += dy;
    }
    // A boar never goes over the cliff.
    const d = isqrt(b.x * b.x + b.y * b.y);
    if (d > out) {
      b.x = idiv(b.x * out, d);
      b.y = idiv(b.y * out, d);
      b.panic = 0;
      b.rush = 0;
    }
    // Nightmares close by hurt it, each one now and then.
    if (s.tick % r.hurt.every === 0) for (const e of live) if (withinRange(e.x, e.y, b.x, b.y, r.hurt.range)) b.hp -= r.hurt.damage;
    if (b.hp <= 0) {
      st.lost++;
      ev(s, { t: 'herd', kind: 'lost', x: b.x, y: b.y });
      continue;
    }
    if (withinRange(b.x, b.y, r.pen.x, r.pen.y, r.penR)) {
      const at = penSpot(r.pen, r.penR, st.penned);
      b.x = at.x;
      b.y = at.y;
      b.penned = true;
      b.moving = false;
      b.panic = 0;
      b.rush = 0;
      st.penned++;
      ev(s, { t: 'herd', kind: 'pen', x: b.x, y: b.y });
      keep.push(b);
      continue;
    }
    // Alone too long: the wolves smell it, and come out of the field's edge beyond it.
    b.alone = loose.some((o) => o !== b && !o.penned && withinRange(o.x, o.y, b.x, b.y, r.lone.range)) ? 0 : b.alone + 1;
    if (b.alone >= r.lone.ticks && st.wolfCd === 0) {
      st.wolfCd = r.lone.cd;
      b.alone = 0;
      const [ex, ey] = scaled(b.x || 1, b.y, out);
      for (let i = 0; i < r.lone.wolves; i++) {
        const e = spawnEnemy(s, 'wolf', 1, false, 0, ex + i * 300, ey - i * 300);
        if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, idiv(e.maxHp * s.rules.enemyHpBp, 10000));
      }
      ev(s, { t: 'herd', kind: 'wolves', x: ex, y: ey });
    }
    keep.push(b);
  }
  st.boars = keep;
};

/** Every boar penned or fallen: won with enough in the pen; lost as soon as too few are left to make it. */
export const herdResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.herd;
  const st = s.herd;
  if (!r || !st) return null;
  const loose = st.boars.length - st.penned;
  if (st.penned + loose < r.need) return 'lost';
  return loose === 0 ? 'won' : null;
};

/**
 * On the drive there is no node to break: a nightmare hunts the nearest boar still out, and stands over it once it
 * reaches it (what it does to it is `hurt`, above). A hero within `guard` of it, or closer than any boar, is hunted
 * instead (false: the usual chase).
 */
export const herdChase = (s: BattleState, e: Enemy, px: number): boolean => {
  const r = s.rules?.herd;
  const st = s.herd;
  if (!r || !st || e.taunter !== 0) return false;
  let tgt: Boar | null = null;
  let bd = 0x7fffffff;
  for (const b of st.boars) {
    if (b.penned) continue;
    const d = dist2(e.x, e.y, b.x, b.y);
    if (d < bd) {
      bd = d;
      tgt = b;
    }
  }
  if (!tgt) return false;
  // A hero near it, or nearer than the boar, draws it off: the heroes are the herd's dogs.
  for (const h of s.heroes) if (h.downTicks === 0 && (withinRange(e.x, e.y, h.x, h.y, r.guard) || dist2(e.x, e.y, h.x, h.y) < bd)) return false;
  e.tgtKind = 0;
  e.tgtId = 0;
  if (withinRange(e.x, e.y, tgt.x, tgt.y, idiv(r.hurt.range * 2, 3))) {
    e.anim = 'attack';
    e.facing = iatan2(tgt.y - e.y, tgt.x - e.x);
    return true;
  }
  e.anim = 'walk';
  stepToward(e, tgt.x, tgt.y, px);
  return true;
};
